import { SimpleSearchFilter } from 'module/api/filter/types';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import {
  ToolbarButtonIcon,
  ToolbarDropdownTriggerButton,
} from 'module/ui/components/atoms/rich-text-editor/toolbar/components';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import {
  faAlignLeft,
  faUserGroupSimple,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ParticipantGroupOption } from 'api/custom-section/types';
import { useExternalTeamMemberFieldsAsFilterableField } from 'helpers/fields/external-team-members-fields/fields';
import { keyBy } from 'lodash';
import { useAccountInitialDataQuery } from 'queries/account';
import React, { useMemo, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PromptType } from 'components/organisms/document-editor/extensions/custom-section/types';
import { CustomFilterTag } from '../section-styles';
import { ParticipantFilterDrawer } from './filter';

const ParticipantFilterOptions = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 125px;
`;

interface FilterProp {
  participants: string;
  defaultValue?: string;
  setPrompt: (prompt: any) => void;
  prompt: PromptType;
  options: any;
}

export const ParticipantFilter = ({
  participants,
  defaultValue,
  options,
  setPrompt,
  prompt,
}: FilterProp) => {
  const { fields: PeopleFields, loading: isLoadingPeopleFields } =
    useExternalTeamMemberFieldsAsFilterableField();

  const { data: accountData } = useAccountInitialDataQuery();
  const accountMeta = accountData?.accountMeta;
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const userFieldLookup = useMemo(
    () => keyBy(PeopleFields, 'fieldName'),
    [PeopleFields],
  );
  const groupedLookupsByName: GroupFieldsMeta = useMemo(() => {
    if (isLoadingPeopleFields) {
      return [];
    }
    let fieldsMeta = [
      {
        id: FilterGroupType.User,
        fields: userFieldLookup,
      },
    ];

    return fieldsMeta;
  }, [userFieldLookup, isLoadingPeopleFields]);
  const groupedTransformer = useMemo(
    () =>
      getApiGroupedFilterTransformer(
        groupedLookupsByName,
        FilterGroupType.User,
      ),
    [groupedLookupsByName],
  );
  const openFilterDrawer = useCallback(() => {
    setIsFilterDrawerVisible(true);
  }, []);

  const closeFilterDrawer = useCallback(() => {
    setIsFilterDrawerVisible(false);
  }, []);

  const onCloseCustomFilterTag = useCallback(() => {
    setFilter(defaultGroupedFilter(FilterGroupType.User));
    setPrompt({
      ...prompt,
      participants: defaultValue,
      participantsFilter: {},
    });
  }, [setPrompt, defaultValue, prompt]);

  const constructGroupedFilter = useCallback(
    (filter: SimpleSearchFilter) => {
      const apiFilter = filter;

      if (apiFilter && !isLoadingPeopleFields) {
        return groupedTransformer.from(apiFilter as SimpleSearchFilter);
      }

      return defaultGroupedFilter(FilterGroupType.User);
    },
    [groupedTransformer, isLoadingPeopleFields],
  );

  const [filter, setFilter] = useState<GroupedFilter>(
    prompt?.participantsFilter && !isLoadingPeopleFields
      ? constructGroupedFilter(prompt?.participantsFilter as SimpleSearchFilter)
      : defaultGroupedFilter(FilterGroupType.User),
  );

  const ParticipantsDropdownMenuProps = useMemo(() => {
    const filteredParticipants = options.filter(
      (option) => option !== ParticipantGroupOption.Custom,
    );
    const subMenuItems = filteredParticipants.map((option) => ({
      key: option,
      label: (
        <FormattedMessage
          id={`rich_text_editor.sections.participant_group.${option}`.toLowerCase()}
          values={{
            accountName: accountMeta?.company?.companyName,
          }}
        />
      ),
    }));
    const commandMenuItems = [
      {
        type: 'group',
        key: 'partcipation_general_filters',
        label: (
          <FormattedMessage id="rich_text_editor.sections.participant_group" />
        ),
        icon: <FontAwesomeIcon icon={faAlignLeft} />,
        children: subMenuItems,
      },
      {
        type: 'divider',
        key: 'partcipation_general_filters_divider',
      },
      {
        key: 'partcipation_custom_filters',
        label: <FormattedMessage id="custom_filters" />,
      },
    ];

    return {
      selectedKeys: [prompt.participants || defaultValue],
      onClick: (e) => {
        if (e.key === 'partcipation_custom_filters') {
          openFilterDrawer();
        } else {
          setPrompt({ ...prompt, participants: e.key });
        }
      },
      items: commandMenuItems as ItemType[],
    };
  }, [
    options,
    prompt,
    defaultValue,
    openFilterDrawer,
    setPrompt,
    accountMeta?.company?.companyName,
  ]);

  const onDone = useCallback(
    (filter: GroupedFilter) => {
      setFilter(filter);
      setPrompt({
        ...prompt,
        participants: ParticipantGroupOption.Custom,
        participantsFilter: groupedTransformer.to(filter),
      });
      closeFilterDrawer();
    },
    [closeFilterDrawer, prompt, setPrompt, groupedTransformer],
  );

  return (
    <>
      {participants === ParticipantGroupOption.Custom &&
      !isLoadingPeopleFields ? (
        <div>
          <FontAwesomeIcon icon={faUserGroupSimple} />
          <CustomFilterTag
            color="blue"
            variant="basic"
            onClick={openFilterDrawer}
            onClose={onCloseCustomFilterTag}
            closable={true}
          >
            <FormattedMessage
              id="custom_filters_condidion"
              values={{
                count: filter?.groupCriteria?.length ?? 0,
              }}
            />
          </CustomFilterTag>
        </div>
      ) : (
        <Dropdown menu={ParticipantsDropdownMenuProps}>
          <ToolbarDropdownTriggerButton
            icon={<ToolbarButtonIcon icon={faUserGroupSimple} />}
          >
            <ParticipantFilterOptions>
              <FormattedMessage
                id={`rich_text_editor.sections.participant_group.${participants || defaultValue}`.toLowerCase()}
                values={{
                  accountName: accountMeta?.company?.companyName,
                }}
              />
            </ParticipantFilterOptions>
          </ToolbarDropdownTriggerButton>
        </Dropdown>
      )}
      <ParticipantFilterDrawer
        onDone={onDone}
        filter={filter}
        isFilterDrawerVisible={isFilterDrawerVisible}
        closeFilterDrawer={closeFilterDrawer}
      />
    </>
  );
};
